"use client";

import {
  AudioLines,
  Check,
  ChevronDown,
  LoaderCircle,
  Music,
  Pause,
  Play,
  Plus,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast as sonnerToast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  createFieldLabelClassName,
  createFieldLabelTextClassName,
} from "./CreatePageShell";

export type SearchableAudioPickerItem = {
  description?: string;
  descriptionAction?: { label: string; onClick: () => void };
  id: string;
  label: string;
  previewable?: boolean;
  previewUrl?: string;
  searchText?: string;
};

export function SearchableAudioPickerField({
  disabled = false,
  emptyLabel = "No matching audio.",
  items,
  icon = "waveform",
  label,
  listLabel,
  onValueChange,
  previewContextKey,
  previewErrorLabel = "This audio preview could not play.",
  searchPlaceholder,
  triggerDescription,
  value,
  action,
  header,
}: {
  disabled?: boolean;
  emptyLabel?: string;
  items: readonly SearchableAudioPickerItem[];
  icon?: "music" | "waveform";
  label: string;
  listLabel: string;
  onValueChange: (value: string) => void;
  previewContextKey?: string;
  previewErrorLabel?: string;
  searchPlaceholder: string;
  triggerDescription?: string;
  value: string;
  header?: ReactNode;
  action?: { label: string; onClick: () => void } | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [loadingPreviewId, setLoadingPreviewId] = useState<string | null>(null);
  const [playingPreviewId, setPlayingPreviewId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const selectedItem = items.find((item) => item.id === value) ?? null;
  const AudioPickerIcon = icon === "music" ? Music : AudioLines;
  const visibleItems = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase("en-US");

    return items.filter(
      (item) =>
        !normalizedQuery ||
        `${item.label} ${item.description ?? ""} ${item.searchText ?? ""}`
          .toLocaleLowerCase("en-US")
          .includes(normalizedQuery),
    );
  }, [items, query]);

  function stopPreview() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
      audioRef.current = null;
    }

    setLoadingPreviewId(null);
    setPlayingPreviewId(null);
  }

  useEffect(() => stopPreview, [previewContextKey, value]);

  async function togglePreview(item: SearchableAudioPickerItem) {
    if (!item.previewUrl) return;
    if (loadingPreviewId) return;
    if (playingPreviewId === item.id) {
      stopPreview();
      return;
    }

    let errorReported = false;
    function reportPreviewError() {
      if (errorReported) return;
      errorReported = true;
      sonnerToast.error(previewErrorLabel);
    }

    try {
      stopPreview();
      setLoadingPreviewId(item.id);
      const audio = new Audio(item.previewUrl);
      audio.preload = "auto";
      audioRef.current = audio;
      audio.addEventListener("ended", stopPreview, { once: true });
      audio.addEventListener(
        "error",
        () => {
          stopPreview();
          reportPreviewError();
        },
        { once: true },
      );
      await audio.play();
      setLoadingPreviewId(null);
      setPlayingPreviewId(item.id);
    } catch (error: unknown) {
      stopPreview();
      if (!(error instanceof DOMException && error.name === "NotAllowedError")) {
        reportPreviewError();
      }
    }
  }

  return (
    <div className={createFieldLabelClassName}>
      <span className={createFieldLabelTextClassName}>{label}</span>
      <Popover
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setQuery("");
        }}
        open={open}
      >
        <PopoverTrigger asChild>
          <button
            aria-expanded={open}
            aria-haspopup="listbox"
            className="group flex h-14 min-h-14 max-h-14 w-full items-center gap-3 rounded-2xl border border-border bg-white/[0.035] px-3 py-2 text-left shadow-sm outline-none transition duration-200 hover:border-white/20 hover:bg-white/[0.055] active:scale-[0.99] focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/35 disabled:pointer-events-none disabled:opacity-55"
            disabled={disabled}
            type="button"
          >
            <span className="flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-white/[0.07] text-[var(--rm-text-soft)]">
              <AudioPickerIcon aria-hidden="true" className="size-5" />
            </span>
            <span className="grid min-w-0 flex-1 content-center">
              <span className="block h-5 truncate text-sm font-semibold leading-5 text-foreground">
                {selectedItem?.label ?? `Choose ${label.toLocaleLowerCase("en-US")}`}
              </span>
              <span className="block h-4 truncate text-xs leading-4 text-[var(--rm-text-muted)]">
                {triggerDescription ?? selectedItem?.description ?? "Select audio"}
              </span>
            </span>
            <ChevronDown
              aria-hidden="true"
              className={cn(
                "size-4 shrink-0 text-[var(--rm-text-muted)] transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex max-h-[min(560px,var(--radix-popover-content-available-height),calc(100dvh-24px))] w-[min(430px,calc(100vw-32px))] flex-col overflow-hidden rounded-[20px] border-white/10 bg-popover p-0 shadow-2xl shadow-black/35"
          sideOffset={8}
        >
          {header}
          <div className="relative shrink-0 border-b border-white/[0.07]">
            <Search
              aria-hidden="true"
              className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[var(--rm-text-muted)]"
            />
            <input
              autoFocus
              className="h-12 w-full bg-transparent pl-11 pr-4 text-sm outline-none placeholder:text-[var(--rm-text-muted)]"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              value={query}
            />
          </div>
          <div
            aria-label={listLabel}
            className="min-h-0 max-h-[420px] overscroll-contain overflow-y-auto p-2 touch-pan-y"
            onTouchMoveCapture={(event) => event.stopPropagation()}
            onWheelCapture={(event) => event.stopPropagation()}
            role="listbox"
          >
            {visibleItems.length ? (
              visibleItems.map((item) => {
                const isSelected = item.id === value;
                const isLoading = loadingPreviewId === item.id;
                const isPlaying = playingPreviewId === item.id;

                return (
                  <div
                    aria-selected={isSelected}
                    className={cn(
                      "relative flex items-center gap-2 rounded-[14px] pr-2 transition duration-150 hover:bg-white/[0.065] focus-within:bg-white/[0.065]",
                      isSelected && "bg-white/[0.075]",
                    )}
                    key={item.id}
                    role="option"
                  >
                    <button
                      aria-label={item.label}
                      className="absolute inset-0 rounded-[14px] outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40"
                      onClick={() => {
                        onValueChange(item.id);
                        setOpen(false);
                        setQuery("");
                      }}
                      type="button"
                    />
                    <div className="pointer-events-none flex min-w-0 flex-1 items-center gap-3 px-2.5 py-2.5 text-left">
                      <span className="flex size-9 shrink-0 items-center justify-center rounded-[11px] bg-white/[0.06] text-[var(--rm-text-muted)]">
                        <AudioPickerIcon aria-hidden="true" className="size-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {item.label}
                        </span>
                        {item.description ? (
                          <span className="mt-0.5 flex min-w-0 items-center gap-2 text-xs text-[var(--rm-text-muted)]">
                            <span className="truncate">{item.description}</span>
                            {item.descriptionAction ? (
                              <>
                                <span aria-hidden="true" className="shrink-0 text-white/20">·</span>
                                <button
                                  aria-label={`${item.descriptionAction.label} ${item.label}`}
                                  className="pointer-events-auto relative z-10 shrink-0 rounded-sm font-medium underline underline-offset-4 outline-none transition hover:text-destructive focus-visible:text-destructive focus-visible:ring-2 focus-visible:ring-ring/40"
                                  onClick={() => { setOpen(false); setQuery(""); stopPreview(); item.descriptionAction?.onClick(); }}
                                  type="button"
                                >{item.descriptionAction.label}</button>
                              </>
                            ) : null}
                          </span>
                        ) : null}
                      </span>
                      <Check
                        aria-hidden="true"
                        className={cn(
                          "size-4 shrink-0 text-primary transition-opacity",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                    {item.previewable !== false && item.previewUrl ? (
                      <button
                        aria-label={
                          isPlaying
                            ? `Stop ${item.label} preview`
                            : `Preview ${item.label}`
                        }
                        className="relative z-10 flex size-9 shrink-0 items-center justify-center rounded-xl border border-white/[0.08] text-[var(--rm-text-muted)] outline-none transition hover:border-white/20 hover:bg-white/[0.07] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
                        onClick={() => void togglePreview(item)}
                        type="button"
                      >
                        {isLoading ? (
                          <LoaderCircle className="size-4 animate-spin" />
                        ) : isPlaying ? (
                          <Pause className="size-4" />
                        ) : (
                          <Play className="size-4" />
                        )}
                      </button>
                    ) : null}
                  </div>
                );
              })
            ) : (
              <p className="px-4 py-10 text-center text-sm text-[var(--rm-text-muted)]">
                {emptyLabel}
              </p>
            )}
          </div>
          {action ? (
            <div className="shrink-0 border-t border-white/[0.07] p-2">
              <button className="flex w-full items-center gap-3 rounded-[14px] px-3 py-3 text-left text-sm font-semibold text-primary outline-none transition hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring/40" type="button" onClick={() => { setOpen(false); stopPreview(); action.onClick(); }}>
                <span className="flex size-9 items-center justify-center rounded-[11px] border border-primary/20 bg-primary/10"><Plus className="size-4" /></span>
                {action.label}
              </button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
